<script setup>
import { watch, computed, defineEmits, defineProps, ref } from 'vue'

const emits = defineEmits(['update'])

const props = defineProps({
  data: { type: [null, String], required: true },
  editable: { type: Boolean, required: false, default: false },
  classes: { type: String, required: false }
})

const displayValue = ref('')
const isEditing = ref(false)
const lastCommittedValue = ref('')

// Function to format time for input type="time"
const formatTimeForInput = timeString => {
  if (!timeString) return ''
  const [hours, minutes] = timeString.split(':')
  if (isNaN(hours) || isNaN(minutes)) return ''
  return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`
}

const syncDisplayValue = (value) => {
  const formattedValue = formatTimeForInput(value)
  displayValue.value = formattedValue
  lastCommittedValue.value = formattedValue
}

const commit = () => {
  const nextValue = formatTimeForInput(displayValue.value)
  displayValue.value = nextValue
  isEditing.value = false

  if (nextValue === lastCommittedValue.value) {
    return
  }

  lastCommittedValue.value = nextValue
  emits('update', nextValue)
}

const commitAndBlur = event => {
  commit()
  event?.target?.blur?.()
}

// Watch for changes in props.data and update displayValue
watch(
  () => props.data,
  newValue => {
    if (isEditing.value) return
    syncDisplayValue(newValue)
  },
  { immediate: true },
)

const computedDisplayValue = computed(() => {
  if (!props.data) return ''
  const [hours, minutes] = props.data.split(':')
  if (isNaN(hours) || isNaN(minutes)) return ''
  return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`
})
</script>

<template>
  <input
    v-if="editable"
    type="time"
    v-model="displayValue"
    :disabled="!props.editable"
    :class="props.classes"
    @focus="isEditing = true"
    @blur="commit"
    @keydown.enter.prevent="commitAndBlur"
  />
  <span v-else>
    <template v-if="props.data">{{ computedDisplayValue }}</template>
  </span>
</template>
